/**
 * Defensive fetch helpers.
 *
 * The platform background:
 *   Workers + Pages Functions return JSON for happy paths, but Cloudflare's
 *   own edge can intercept a request and return an HTML/plaintext error page
 *   ("Your worker exceeded its resource limit", a 502, an outage, etc).
 *   `await response.json()` on those responses throws a SyntaxError, which
 *   surfaces in Sentry as "Unexpected token 'Y', \"Your worke\"..." with no
 *   in-app frames — annoying to debug and a crash in any unguarded caller.
 *
 * Pattern to use in new code:
 *   const data = await safeJson<MyShape>(response);
 *   if (!response.ok || data === null) {
 *     // handle error, with `response.status` and `response.statusText`
 *     return;
 *   }
 *   // use `data`
 *
 * Or, for the common "give me the body or fall through" case:
 *   const body = await readJsonSafely(response);
 */

/**
 * Parse a response as JSON without throwing. Returns null when:
 *   - the response was non-OK (HTTP 4xx/5xx, or a Cloudflare error page)
 *   - the body wasn't valid JSON (likely a CF error page or HTML)
 *   - the body was empty
 *
 * Logs to the console in dev so unexpected non-JSON responses are visible
 * during testing; silent in prod so callers can handle the null path without
 * noise.
 */
export async function safeJson<T = unknown>(response: Response): Promise<T | null> {
  if (!response.ok) return null;
  try {
    // Some endpoints return 204 No Content; treat as null body.
    if (response.status === 204) return null;
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text) as T;
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[safeJson] non-JSON response body:', err);
    }
    return null;
  }
}

/**
 * Variant that returns the parsed body regardless of `response.ok`, so error
 * responses carrying JSON bodies (e.g. `{ ok: false, error: '...' }`) still
 * give the caller something to inspect. Still returns null on parse failure.
 *
 * Use this when you want to surface both success and structured-error payloads
 * to the caller (`response.ok` is checked separately).
 */
export async function readJsonSafely<T = unknown>(response: Response): Promise<T | null> {
  try {
    if (response.status === 204) return null;
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text) as T;
  } catch (err) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[readJsonSafely] non-JSON response body:', err);
    }
    return null;
  }
}
